import React, { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import empty from "is-empty";

import {
    SettingsSelectors,
    UiPeerCptySettings,
} from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { ModalActions } from "src/redux/Modal/actions";
import { SettingsActions } from "src/redux/Settings/actions";
import Button from "src/components/common/Button";
import TruTooltip from "src/components/common/AgGrid/TruTooltip";
import {
    defaultColDef,
    peerCptyColumnDefs,
    isEveryElementUnique,
    isCptyCodeValid,
    isCptyNameValid,
    isCptyCodePrefixed,
    EXT_CPTY_PREFIX,
} from "./column_defs";
import {
    MultiEmailsEditor,
    MultiEmailsRenderer,
} from "src/components/common/AgGrid/MultiEmails";

import styles from "./styles.module.scss";
import * as base32 from "hi-base32";
import * as secureRandom from "secure-random";
import ManageSecretKey from "../../ManageSecretKey";

const removeKeyFromImmutable = (obj, key) => {
    const newObj = { ...obj };
    delete newObj[key];
    return newObj;
};

// todo: replace this with common/Button once we figure out how to nest it inside a column
class SecretButton {
    init(params) {
        this.eGui = document.createElement("div");
        this.eGui.className = styles.secretBtn;
        if (params.data.isUnregisteredCpty) {
            this.element = document.createElement("button");
            this.element.className = `${styles.secret} secret-btn`;
            this.element.textContent = "Copy Secret";
            // setAttribute keeps the cpty code inert - never parsed as HTML.
            this.element.setAttribute("data-testid", `copy-secret-${params.data?.cptyCode}`);
            this.element.onclick = () =>
                navigator.clipboard.writeText(params.data.secret);
            this.eGui.appendChild(this.element);
        }
    }

    getGui() {
        return this.eGui;
    }
}

class DeleteButton {
    init(params) {
        this.eGui = document.createElement("div");
        this.eGui.className = styles.deleteBtn;
        if (params.data.isUnregisteredCpty) {
            // allow deleteing only external peer cptys
            this.element = document.createElement("div");
            this.element.className = `${styles.delete} delete-btn`;
            // setAttribute keeps the cpty code inert - never parsed as HTML.
            this.element.setAttribute("data-testid", `delete-${params.data?.cptyCode}`);
            this.element.onclick = () => this.handleDelete(params);
            this.eGui.appendChild(this.element);
        }
    }

    handleDelete(params) {
        params.context.deleteRow(
            params.data.isNew,
            params.rowIndex,
            params.data.cptyCode
        );
    }

    getGui() {
        return this.eGui;
    }
}

const PeerCptySettings = () => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);
    const [peerCptys, setPeerCptys] = useState(useSelector(UiPeerCptySettings));
    const [newPeerCptys, setNewPeerCptys] = useState(
        useSelector(SettingsSelectors.NewPeerCptySetting)
    );
    const [changeSet, setChangeSet] = useState({});
    const [deleteSet, setDeleteSet] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const [shouldRefresh, setShouldRefresh] = useState(false);
    const {
        "enable-lender-add-borrowers": enabledLenderAddBorrowers,
        "enable-create-totp-secret": allowCreateSecret,
        "enable-reset-totp-secret": enableResetTotpSecret,
        // Limited UI: Guest Lender flow is out of scope per spec §3.
        // The Add Counterparty path creates unregistered (guest) cpties,
        // so hide it. Backend also rejects via SettingsController guard.
        "limited-ui": limitedUi,
    } = useSelector(SettingsSelectors.FeatureFlags);

    const allSettings = useMemo(
        () =>
            peerCptys
                .map((s) => changeSet[s.cptyCode] ?? s)
                .concat(newPeerCptys)
                // Spec §3 "Guest Lender: Disabled". Hide any
                // pre-existing unregistered cpty rows under limited-ui
                // — the user can't interact with them (Add Counterparty
                // is hidden, SelectAll selector excludes them from
                // pickers), so showing them only confuses the surface.
                .filter((s) => !(limitedUi && s.isUnregisteredCpty)),
        [peerCptys, newPeerCptys, changeSet, limitedUi]
    );
    const externalSettings = useMemo(
        () => allSettings.filter((cpty) => cpty.isUnregisteredCpty),
        [allSettings]
    );
    const allCptyCodes = useMemo(
        () => allSettings.map((c) => c.cptyCode),
        [allSettings]
    );
    const allNames = useMemo(
        () => allSettings.map((c) => c.cptyName),
        [allSettings]
    );

    const canAddOrSave = useMemo(() => {
        return (
            newPeerCptys.every(
                (cpty) =>
                    isCptyCodeValid(cpty.cptyCode) &&
                    isCptyNameValid(cpty.cptyName)
            ) &&
            externalSettings.every((cpty) => !empty(cpty.emails)) &&
            isEveryElementUnique(allCptyCodes) &&
            isEveryElementUnique(allNames)
        );
    }, [newPeerCptys, externalSettings, allCptyCodes, allNames]);

    const errorMessage = useMemo(() => {
        if (
            !newPeerCptys.every(
                (cpty) =>
                    isCptyCodePrefixed(cpty.cptyCode) &&
                    cpty.cptyCode.length <= 20
            )
        ) {
            return `Code should be 5 to 15 character long and start with '${EXT_CPTY_PREFIX}'.`;
        }
        if (
            !newPeerCptys.every((cpty) => cpty.cptyCode.match(/^[A-Z0-9-]+$/g))
        ) {
            return "Code should contain only upper-case letters, numbers, and hyphen(-)";
        } else if (!isEveryElementUnique(allCptyCodes)) {
            return "Code must be unique.";
        } else if (
            !newPeerCptys.every((cpty) => isCptyNameValid(cpty.cptyName))
        ) {
            return "Name should be 1 to 50 characters long.";
        } else if (!isEveryElementUnique(allNames)) {
            return "Name must be unique.";
        } else if (!externalSettings.every((cpty) => !empty(cpty.emails))) {
            return "Unregistered cptys must have at least one e-mail address.";
        }
        return "";
    }, [newPeerCptys, externalSettings, allCptyCodes, allNames]);

    const removeOrReplaceNewCptyAt = useCallback(
        (index, replacement) => {
            let tempNewCptys = [...newPeerCptys];
            if (!empty(replacement)) {
                tempNewCptys[index - peerCptys.length] = replacement;
            } else {
                tempNewCptys.splice(index - peerCptys.length, 1);
            }

            return tempNewCptys;
        },
        [peerCptys, newPeerCptys]
    );

    const handleCellValueChanged = useCallback(
        (params) => {
            if (params.data.isNew) {
                setNewPeerCptys(
                    removeOrReplaceNewCptyAt(params.rowIndex, {
                        ...params.data,
                    })
                );
                dispatch(SettingsActions.SetNewPeerCptySetting(newPeerCptys));
                return;
            }

            setChangeSet({
                ...changeSet,
                [params.data.cptyCode]: { ...params.data },
            });
            params.api.redrawRows();
        },
        [changeSet, removeOrReplaceNewCptyAt]
    );

    const handleDeleteRow = useCallback(
        (isNew, rowIndex, cptyCode) => {
            if (isNew) {
                // delete new external cptys
                setNewPeerCptys(removeOrReplaceNewCptyAt(rowIndex));
            } else {
                // delete existing external cptys
                setPeerCptys(
                    peerCptys
                        .slice(0, rowIndex)
                        .concat(peerCptys.slice(rowIndex + 1))
                );
                setDeleteSet(deleteSet.concat(cptyCode));
                setChangeSet(removeKeyFromImmutable(changeSet, cptyCode));
            }
        },
        [peerCptys, deleteSet, removeOrReplaceNewCptyAt, changeSet]
    );

    const handleAddNewRow = useCallback(() => {
        let input = secureRandom(32);
        let secret = base32.encode(input).slice(0, 16);

        setNewPeerCptys(
            newPeerCptys.concat({
                cptyCode: EXT_CPTY_PREFIX,
                cptyName: "",
                defaultObjective: "",
                isUnregisteredCpty: true,
                emails: [],
                isNew: true,
                secret: secret,
            })
        );

        setShouldRefresh(true);
    }, [setNewPeerCptys, newPeerCptys]);

    const handleRowDataChanged = useCallback(
        (e) => {
            if (gridApi && shouldRefresh) {
                const index = gridApi.getDisplayedRowCount() - 1;
                gridApi.ensureIndexVisible(index, "bottom");
                setShouldRefresh(false);
            }
        },
        [gridApi, shouldRefresh]
    );

    const handleGridReady = useCallback(
        (params) => {
            setGridApi(params.api);
        },
        [setGridApi]
    );
    const handleManageSecretKey = (params) => {
        dispatch(SettingsActions.SetSelectedPeerCptySetting(params.data));
        dispatch(
            ModalActions.Open(ManageSecretKey, {
                width: 500,
                ignoreClickOutside: true,
                title: "Manage Secret Key",
            })
        );
    };
    const ManageSecretButtonRenderer = (params) =>
        params.data.isUnregisteredCpty ? (
            <Button
                onClick={() => handleManageSecretKey(params)}
                dataTestId="manage-secret-key"
                value="Manage Secret"
            />
        ) : null;

    const cancelAddCounterParty = () => {
        dispatch(SettingsActions.SetNewPeerCptySetting([]));
        dispatch(ModalActions.Close());
    };

    return (
        <div className={styles.peerCptySettingsWrapper}>
            <div
                className={styles.peerCptySettings}
                data-testid="peer-cpty-settings"
            >
                <div
                    className={[styles.table, "ag-theme-alpine-dark"].join(" ")}
                >
                    <AgGridReact
                        rowBuffer={50}
                        rowHeight={40}
                        headerHeight={40}
                        rowData={allSettings}
                        defaultColDef={defaultColDef}
                        columnDefs={peerCptyColumnDefs(allowCreateSecret)}
                        enableCellChangeFlash={true}
                        onCellValueChanged={handleCellValueChanged}
                        suppressContextMenu={true}
                        scrollbarWidth={8}
                        suppressHorizontalScroll={true}
                        components={{
                            deleteButton: DeleteButton,
                            secretButton: enableResetTotpSecret
                                ? ManageSecretButtonRenderer
                                : SecretButton,
                            multiEmailsRenderer: MultiEmailsRenderer,
                            multiEmailsEditor: MultiEmailsEditor,
                            txTooltip: TruTooltip,
                        }}
                        tooltipShowDelay={1}
                        context={{ deleteRow: handleDeleteRow, allSettings }}
                        onGridReady={handleGridReady}
                        onRowDataChanged={handleRowDataChanged}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                    />
                </div>
            </div>
            <div className={styles.controls}>
                {!empty(errorMessage) && (
                    <div
                        className={styles.errorMessage}
                        data-testid={"new-peer-cpty-error-message"}
                    >
                        {errorMessage}
                    </div>
                )}
            </div>
            <div className={styles.bottomContainer}>
                {enabledLenderAddBorrowers && !limitedUi && (
                    <Button
                        dataTestId="add-peer-cpty"
                        value="Add Counterparty"
                        color="reversedWhite"
                        disabled={!canAddOrSave}
                        onClick={handleAddNewRow}
                    />
                )}
                <div className={styles.bottom}>
                    <Button
                        onClick={cancelAddCounterParty}
                        color="reversedWhite"
                        dataTestId="close-peer-cpty-settings"
                        value="Cancel"
                    />
                    <Button
                        onClick={() =>
                            dispatch(
                                SettingsActions.SavePeerCptySettings(
                                    changeSet,
                                    newPeerCptys,
                                    deleteSet
                                )
                            )
                        }
                        dataTestId="save-peer-cpty-settings"
                        value="Save"
                        disabled={!isTraderRole || !canAddOrSave}
                    />
                </div>
            </div>
        </div>
    );
};

export default PeerCptySettings;
